import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { useDataTable } from '@/components/common/data-table/use-data-table';

import { ColumnDef } from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { useListQuoteSheets } from '@/api/procurement/useListQuoteSheets';
import {
  QuoteSheetDTO,
  QuoteSheetFilters,
} from '@/api/procurement/quote-sheets-types';
import { useState, useCallback, useMemo, memo } from 'react';
import { QuoteSheetInboxProductsView } from '../QuoteSheetProductsView';
import { format } from 'date-fns';
import { DateRangePicker } from '@/components/common/date-pickers/date-range-picker';
import { DateRange } from 'react-day-picker';
import { ProductsV2Autocomplete } from '@/components/products/ProductsV2Autocomplete';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';
import { SortDescendingIcon } from '@phosphor-icons/react';
import { useEagerAndDebouncedState } from '@/hooks/useEagerDebouncedState';
import { TableSkeleton } from '@/components/common/loading-skeletons/table-skeleton';
import Page from '@/components/page/Page';
import { Outlet, useNavigate } from '@tanstack/react-router';
import SupplierPill from '@/components/company/SupplierPill';

const columns: ColumnDef<QuoteSheetDTO>[] = [
  {
    id: 'importedDateTime',
    header: () => {
      return (
        <div className="flex items-center gap-1 justify-between">
          Imported At
          <SortDescendingIcon className="size-4" />
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <div>
          {format(new Date(row.original.createdAt), 'MM/dd/yyyy HH:mm a')}
        </div>
      );
    },
    size: 180,
  },
  {
    id: 'quoteDate',
    header: 'Quote Date',
    cell: ({ row }) => {
      return (
        <div>
          {format(
            new Date(row.original.quoteDate ?? row.original.createdAt),
            'MM/dd/yyyy',
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'vendor',
    id: 'vendor',
    header: 'Vendor',
    cell: ({ row }) => {
      const vendor = row.original.supplier;
      return <SupplierPill supplier={vendor} />;
    },
  },
  {
    accessorKey: 'products',
    id: 'products',
    header: 'Products ',
    cell: ({ row, table }) => {
      return (
        <QuoteSheetInboxProductsView
          quoteSheet={row.original}
          filter={table.getState().globalFilter}
        />
      );
    },
  },
];

// Memoized table component to prevent unnecessary re-renders
const QuoteSheetTable = memo(function QuoteSheetTable({
  filters,
  quoteSheets,
  onRowClick,
  onFiltersChange,
}: {
  filters: QuoteSheetFilters;
  quoteSheets: QuoteSheetDTO[];
  onRowClick: (quoteSheet: QuoteSheetDTO) => void;
  onFiltersChange: (filters: QuoteSheetFilters) => void;
}) {
  // Use search value directly from filters for immediate UI updates
  const searchValue = filters.search ?? '';

  const table = useDataTable({
    columns,
    data: quoteSheets,
    options: {
      initialState: {
        pagination: { pageSize: 25 },
      },
    },
  });

  // Update table's global filter immediately with search value
  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      table.setGlobalFilter(value);

      // Update filters for API call
      onFiltersChange({
        ...filters,
        search: value,
      });
    },
    [filters, onFiltersChange, table],
  );

  const onDateRangeChange = useCallback(
    (dateRange: DateRange | undefined) => {
      onFiltersChange({
        ...filters,
        dateFrom: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        dateTo: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      });
    },
    [filters, onFiltersChange],
  );

  const onProductSelect = useCallback(
    (productId: string | undefined) => {
      onFiltersChange({
        ...filters,
        productId,
      });
    },
    [filters, onFiltersChange],
  );

  const onSupplierSelect = useCallback(
    (supplierId: string | undefined) => {
      onFiltersChange({
        ...filters,
        supplierId,
      });
    },
    [filters, onFiltersChange],
  );

  // Check if any custom filters are active
  const isCustomFiltered = useMemo(() => {
    return Boolean(
      filters.search ||
        filters.productId ||
        filters.supplierId ||
        filters.dateFrom ||
        filters.dateTo,
    );
  }, [filters]);

  // Clear all custom filters
  const onClearFilters = useCallback(() => {
    onFiltersChange({});
  }, [onFiltersChange]);

  return (
    <div className="space-y-4">
      <Card>
        <DataTableToolbar
          table={table}
          search={{
            placeholder: 'Search products',
            value: searchValue,
            onChange: onSearchChange,
          }}
          onClearFilters={onClearFilters}
          hasCustomFilters={isCustomFiltered}
        >
          <ProductsV2Autocomplete
            selectedProductId={filters.productId}
            onProductSelect={onProductSelect}
            allowClear
          />
          <SupplierAutocomplete
            supplierId={filters.supplierId}
            onSelect={onSupplierSelect}
            className="w-fit"
          />
          <DateRangePicker
            value={{
              from: filters.dateFrom ? new Date(filters.dateFrom) : undefined,
              to: filters.dateTo ? new Date(filters.dateTo) : undefined,
            }}
            onChange={onDateRangeChange}
            className="w-fit"
          />
        </DataTableToolbar>
      </Card>
      <DataTable
        className="bg-background"
        table={table}
        onRowClick={onRowClick}
      />
      <DataTablePagination table={table} />
    </div>
  );
});

function PriceQuoteSheetInbox() {
  const [filters, setFilters] = useState<QuoteSheetFilters>({});
  // Separate search state management for immediate UI updates and debounced API calls
  const {
    eagerValue: searchValue,
    debouncedValue: debouncedSearch,
    setValue: setSearchValue,
  } = useEagerAndDebouncedState('', 300);

  const apiFilters = useMemo(
    () => ({
      ...filters,
      search: debouncedSearch || undefined, // Only send non-empty search values to API
    }),
    [filters, debouncedSearch],
  );

  // Handle other filter changes
  const onFiltersChange = useCallback(
    (newFilters: QuoteSheetFilters) => {
      console.log('newFilters', newFilters);
      setFilters(newFilters);
      setSearchValue(newFilters.search ?? '');
    },
    [setSearchValue],
  );

  const { data } = useListQuoteSheets(apiFilters);
  const navigate = useNavigate();

  const onRowClick = useCallback(
    (quoteSheet: QuoteSheetDTO) => {
      navigate({
        to: '/price-sheet-inbox/$quoteSheetId',
        params: { quoteSheetId: quoteSheet.id },
      });
    },
    [navigate],
  );

  // Memoize quote sheets to prevent unnecessary re-renders
  const quoteSheets = useMemo(
    () => data?.quoteSheets ?? [],
    [data?.quoteSheets],
  );

  if (!data?.quoteSheets) {
    return <TableSkeleton columns={columns.length} rows={25} />;
  }

  return (
    <>
      <QuoteSheetTable
        filters={{ ...filters, search: searchValue }}
        quoteSheets={quoteSheets}
        onRowClick={onRowClick}
        onFiltersChange={onFiltersChange}
      />
    </>
  );
}

export function PriceQuoteSheetInboxPage() {
  return (
    <Page title="Price Sheet Inbox">
      <PriceQuoteSheetInbox />
      <Outlet />
    </Page>
  );
}
